import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleLayout, { blogStyles as s } from './BlogArticleLayout';

function BlogArticleRgpdNis2Iso27001() {
  return (
    <BlogArticleLayout tag="Cybersécurité & Conformité" title="RGPD, NIS2, ISO 27001 : quelles différences ?" date="1 septembre 2026">
      <p style={s.intro}>
        RGPD, NIS2, ISO 27001 : trois noms qui reviennent sans cesse dès qu'on parle de cybersécurité en
        entreprise, et qui finissent par se mélanger dans la tête d'un dirigeant. Pourtant, ces trois
        référentiels ne poursuivent pas le même objectif, n'ont pas le même statut, et ne couvrent pas le
        même périmètre. Comprendre ces différences est la première étape pour savoir ce qui s'applique
        réellement à votre entreprise, et éviter de vous lancer dans une démarche mal ciblée.
      </p>

      <h2 style={s.h2}>Qu'est-ce que le RGPD impose réellement en matière de cybersécurité ?</h2>
      <p>
        Le RGPD (règlement général sur la protection des données) n'est pas, à l'origine, un texte de
        cybersécurité : c'est un texte de protection des données personnelles. Mais il impose une obligation
        de sécurité précise, connue sous le nom de « sécurité du traitement » : toute entreprise qui traite
        des données personnelles (salariés, clients, prospects) doit mettre en place des mesures techniques
        et organisationnelles proportionnées au risque pour protéger ces données.
      </p>
      <p>
        Concrètement, cela couvre par exemple le chiffrement, la gestion des accès, les sauvegardes, ou
        encore la capacité à notifier une violation de données à la CNIL sous 72 heures. Le RGPD s'applique
        dès qu'il y a traitement de données personnelles — ce qui concerne, de fait, la quasi-totalité des
        entreprises, quelle que soit leur taille.
      </p>

      <h2 style={s.h2}>Qui est concerné par NIS2 ?</h2>
      <p>
        NIS2 est une directive européenne dédiée cette fois directement à la cybersécurité des réseaux et
        systèmes d'information, avec un périmètre plus ciblé que le RGPD. Elle vise les entités jugées «
        essentielles » ou « importantes » dans des secteurs considérés comme critiques : énergie, santé,
        transport, banque, infrastructures numériques, administrations, mais aussi, selon les cas, leurs
        sous-traitants et prestataires.
      </p>
      <p>
        Contrairement au RGPD, NIS2 ne concerne donc pas toutes les entreprises : elle dépend du secteur
        d'activité et, souvent, de critères de taille. La transposition du texte en droit français précise
        progressivement ces critères — c'est un point qu'il vaut mieux vérifier au cas par cas plutôt que de
        se fier à une règle générale, tant les situations diffèrent d'un secteur à l'autre. Nous détaillons
        ces critères dans <Link to="/blog/qui-est-concerne-par-nis2" style={s.link}>notre article dédié à l'application de NIS2</Link>.
      </p>

      <h2 style={s.h2}>ISO 27001 est-elle obligatoire ?</h2>
      <p>
        Non. ISO 27001 est une norme internationale de management de la sécurité de l'information : elle
        décrit une méthode (le fameux SMSI, système de management de la sécurité de l'information) pour
        identifier, traiter et suivre les risques de sécurité dans la durée. Aucune loi n'oblige une
        entreprise à l'adopter.
      </p>
      <p>
        En revanche, elle est de plus en plus souvent exigée dans les faits : par un client grand compte,
        dans un appel d'offres public, ou par un partenaire soumis lui-même à NIS2 et qui répercute des
        exigences de sécurité sur sa chaîne de sous-traitance. C'est une démarche volontaire qui devient, pour
        certaines PME, une condition commerciale de facto.
      </p>

      <h2 style={s.h2}>Quelle différence entre obligation légale et certification volontaire ?</h2>
      <p>C'est là que les trois référentiels se distinguent le plus nettement :</p>
      <ul style={s.ul}>
        <li style={s.li}>Le <strong>RGPD</strong> est une obligation légale, sanctionnée par la CNIL, qui s'applique dès qu'il y a données personnelles.</li>
        <li style={s.li}><strong>NIS2</strong> est également une obligation légale, mais réservée aux secteurs et entités identifiés comme critiques.</li>
        <li style={s.li}><strong>ISO 27001</strong> est une certification volontaire : personne ne peut vous sanctionner pour ne pas l'avoir, mais son absence peut vous fermer des portes commerciales.</li>
      </ul>

      <h2 style={s.h2}>Une entreprise peut-elle être concernée par les trois à la fois ?</h2>
      <p>
        Oui, et c'est même une situation fréquente pour une PME/ETI d'un secteur régulé : elle traite des
        données personnelles (donc soumise au RGPD), elle opère dans un secteur ou pour des donneurs d'ordre
        couverts par NIS2, et elle vise ou obtient une certification ISO 27001 pour rassurer ses clients. Les
        trois se cumulent sans se contredire — ils regardent simplement le même sujet, la sécurité de
        l'information, sous trois angles différents.
      </p>

      <h2 style={s.h2}>Faut-il mener trois démarches différentes ?</h2>
      <p>
        En théorie, chaque référentiel a ses propres exigences et son propre formalisme. En pratique,
        non : mener trois démarches indépendantes est le meilleur moyen de tripler la charge de travail pour
        un résultat incohérent. L'approche la plus efficace consiste à identifier le socle de mesures communes
        aux trois référentiels, puis à ajouter les briques spécifiques à chacun.
      </p>

      <h2 style={s.h2}>Quels contrôles et mesures sont communs aux trois référentiels ?</h2>
      <p>
        Le RGPD, NIS2 et ISO 27001 partagent un tronc commun de bonnes pratiques : cartographie des données et
        des systèmes, gestion des accès et des habilitations, sauvegardes et plan de continuité, chiffrement
        des données sensibles, procédure de gestion des incidents, sensibilisation des équipes, et suivi dans
        le temps plutôt qu'un contrôle ponctuel. C'est ce socle commun — décrit plus en détail dans
        notre <Link to="/blog/cybersecurite-pme-guide-dirigeants" style={s.link}>guide cybersécurité PME</Link> — qui justifie une approche unifiée plutôt que trois chantiers séparés.
      </p>

      <h2 style={s.h2}>Comment éviter de multiplier les audits et les plans d'action ?</h2>
      <p>
        La clé est de partir d'un diagnostic unique qui couvre les trois référentiels en même temps, plutôt
        que de faire réaliser trois audits distincts par trois prestataires différents. Un diagnostic
        transversal permet d'identifier une seule fois les écarts, puis de générer un plan d'action qui traite
        les mesures communes en priorité — avant de traiter les exigences propres à chaque référentiel. Voir
        notre <Link to="/blog/audit-cybersecurite-pme-guide-pratique" style={s.link}>guide pratique de l'audit cybersécurité</Link>.
      </p>

      <h2 style={s.h2}>Par quoi commencer quand on est une PME ?</h2>
      <p>
        Avant de choisir un référentiel à suivre à la lettre, la première question à se poser est simple :
        lesquels de ces trois textes s'appliquent réellement à mon entreprise, sur la base de mon secteur, de
        ma taille et des données que je traite ? C'est précisément ce que permet un diagnostic de cybersécurité
        et de conformité : plutôt que de deviner, vous identifiez en quelques minutes les exigences qui vous
        concernent, et vous obtenez un plan d'action structuré pour y répondre progressivement.
      </p>

      <p style={s.disclaimer}>
        Cet article a une vocation informative et ne remplace pas une analyse juridique personnalisée. Les
        critères d'application de NIS2 en particulier dépendent de votre secteur et de votre situation : en
        cas de doute, faites valider votre analyse par un professionnel du droit ou par votre DPO.
      </p>
    </BlogArticleLayout>
  );
}

export default BlogArticleRgpdNis2Iso27001;
